<template>
  <el-container class="exam-container">
    <!-- 左侧：答题卡/得分卡（使用 sticky 粘性定位，使其在全局滚动时固定在左侧） -->
    <el-aside width="280px" class="exam-aside">
      <div class="aside-card answer-card">
        <h4 class="card-title">答题得分卡</h4>
        <div v-for="(section, sIndex) in sectionsList" :key="sIndex" class="card-group">
          <div class="group-label">{{ section.sectionName }}</div>
          <div class="number-grid">
            <span
              v-for="q in section.flatQuestions"
              :key="q.questionId"
              class="number-item"
              :class="getScoreClass(q)"
              @click="scrollToQuestion(q.questionId)"
            >
              {{ q.number }}
            </span>
          </div>
        </div>
        <!-- 侧边栏底部简易比例说明 -->
        <div class="score-legend">
          <span class="legend-item"><i class="dot full" /> 满分</span>
          <span class="legend-item"><i class="dot part" /> 部分得分</span>
          <span class="legend-item"><i class="dot zero" /> 错/零分</span>
        </div>
      </div>
    </el-aside>

    <!-- 右侧：试卷题目及判分详情 -->
    <el-main class="exam-main">
      <div class="paper-header">
        <h2 class="paper-title">{{ paperInfo.title }}</h2>
        <div class="paper-meta">
          <span>测评科目：{{ paperInfo.subjectName || '未指定' }}</span>
          <span class="total-score-banner">
            客观/主观题得分总览请见各题下方
          </span>
        </div>
      </div>

      <div v-for="(section, sIndex) in sectionsList" :key="sIndex" class="question-group-section">
        <h3 class="group-header-title">
          {{ section.sectionName }} <span class="group-desc">（{{ section.sectionHint }}）</span>
        </h3>

        <div
          v-for="q in section.questions"
          :id="'q-' + q.questionId"
          :key="q.questionId"
          class="question-card"
          :class="{ 'is-active': currentActiveId === q.questionId }"
          @mouseenter="currentActiveId = q.questionId"
        >
          <!-- 1. 单选题 -->
          <template v-if="q.typeCode === 1">
            <div class="question-stem">
              <span class="q-number">{{ q.number }}.</span>
              <span class="q-score-highlight">（{{ q.score }}分）</span>
              <span class="q-text" v-html="formatStem(q.content)" />
              <el-tag size="mini" type="info">单选</el-tag>
            </div>
            <div class="question-body">
              <div class="options-display-list">
                <div
                  v-for="opt in q.answers"
                  :key="opt.pos"
                  class="mark-option-item"
                  :class="{
                    'opt-correct': opt.correct,
                    'opt-user-selected': (q.userAnswer || []).includes(opt.pos),
                    'opt-user-wrong': (q.userAnswer || []).includes(opt.pos) && !opt.correct
                  }"
                >
                  <i v-if="opt.correct" class="el-icon-success text-success" />
                  <i v-else-if="(q.userAnswer || []).includes(opt.pos)" class="el-icon-error text-danger" />
                  <i v-else class="el-icon-circle-check text-muted" />
                  <span class="opt-content">{{ opt.content }}</span>
                  <el-tag v-if="opt.correct" size="mini" type="success" effect="dark" class="mini-tag">正确答案</el-tag>
                  <el-tag v-if="(q.userAnswer || []).includes(opt.pos)" size="mini" type="primary" effect="plain" class="mini-tag">考生选择</el-tag>
                </div>
              </div>
              <div class="result-display-bar">
                <div class="score-tag-wrapper">
                  结果判定：
                  <el-tag :type="q.markScore === q.score ? 'success' : 'danger'" effect="dark">
                    得分：{{ q.markScore }} 分 / 满分 {{ q.score }} 分
                  </el-tag>
                </div>
              </div>
            </div>
          </template>

          <!-- 2. 多选题 -->
          <template v-else-if="q.typeCode === 2">
            <div class="question-stem">
              <span class="q-number">{{ q.number }}.</span>
              <span class="q-score-highlight">（{{ q.score }}分）</span>
              <span class="q-text" v-html="formatStem(q.content)" />
              <el-tag type="warning" size="mini" class="type-tag">多选</el-tag>
            </div>
            <div class="question-body">
              <div class="options-display-list">
                <div
                  v-for="opt in q.answers"
                  :key="opt.pos"
                  class="mark-option-item"
                  :class="{
                    'opt-correct': opt.correct,
                    'opt-user-selected': (q.userAnswer || []).includes(opt.pos),
                    'opt-user-wrong': (q.userAnswer || []).includes(opt.pos) && !opt.correct
                  }"
                >
                  <i v-if="opt.correct" class="el-icon-success text-success" />
                  <i v-else-if="(q.userAnswer || []).includes(opt.pos)" class="el-icon-error text-danger" />
                  <i v-else class="el-icon-circle-check text-muted" />
                  <span class="opt-content">{{ opt.content }}</span>
                  <el-tag v-if="opt.correct" size="mini" type="success" effect="dark" class="mini-tag">正确答案</el-tag>
                  <el-tag v-if="(q.userAnswer || []).includes(opt.pos)" size="mini" type="primary" effect="plain" class="mini-tag">考生选择</el-tag>
                </div>
              </div>
              <div class="result-display-bar">
                <div class="score-tag-wrapper">
                  结果判定：
                  <el-tag :type="q.markScore === q.score ? 'success' : (q.markScore > 0 ? 'warning' : 'danger')" effect="dark">
                    得分：{{ q.markScore }} 分 / 满分 {{ q.score }} 分
                  </el-tag>
                </div>
              </div>
            </div>
          </template>

          <!-- 3. 填空题 -->
          <template v-else-if="q.typeCode === 3">
            <div class="question-stem">
              <span class="q-number">{{ q.number }}.</span>
              <span class="q-score-highlight">（{{ q.score }}分）</span>
              <span class="q-text" v-html="formatStem(q.content)" />
              <el-tag size="mini" type="primary">填空</el-tag>
            </div>
            <div class="question-body subjective-mark-box">
              <el-row :gutter="20" class="compare-row">
                <el-col :span="12">
                  <div class="answer-block user-answer-block">
                    <div class="block-title"><i class="el-icon-user" /> 考生作答情况</div>
                    <div class="block-content font-code">
                      {{ (q.userAnswer && q.userAnswer[0]) || '（未作答）' }}
                    </div>
                  </div>
                </el-col>
                <el-col :span="12">
                  <div class="answer-block standard-answer-block">
                    <div class="block-title"><i class="el-icon-key" /> 参考答案</div>
                    <div class="block-content font-code">
                      <div v-for="(opt, oIdx) in q.answers" :key="oIdx">
                        <span class="text-success font-bold">{{ opt.content }}</span>
                      </div>
                    </div>
                  </div>
                </el-col>
              </el-row>
              <!-- 纯文本展示结果与评语 -->
              <div class="result-display-bar display-column">
                <div class="score-display">
                  最终得分：<span class="score-num" :class="q.markScore > 0 ? 'score-win' : 'score-loss'">{{ q.markScore }}</span> / {{ q.score }} 分
                </div>
                <div v-if="q.markReview" class="review-display">
                  <i class="el-icon-chat-dot-round" /> <strong>评语：</strong>{{ q.markReview }}
                </div>
              </div>
            </div>
          </template>

          <!-- 4. 问答题 -->
          <template v-else-if="q.typeCode === 4">
            <div class="question-stem">
              <span class="q-number">{{ q.number }}.</span>
              <span class="q-score-highlight">（{{ q.score }}分）</span>
              <span class="q-text" v-html="formatStem(q.content)" />
              <el-tag size="mini" type="purple">问答</el-tag>
            </div>
            <div class="question-body subjective-mark-box">
              <div class="answer-block user-answer-block mb-15">
                <div class="block-title"><i class="el-icon-user" /> 考生答案</div>
                <div class="block-content text-pre-wrap">{{ (q.userAnswer && q.userAnswer[0]) || '（空白未填写）' }}</div>
              </div>
              <div class="answer-block standard-answer-block mb-15">
                <div class="block-title"><i class="el-icon-key" /> 参考答案</div>
                <div class="block-content text-pre-wrap">{{ q.answers && q.answers[0] ? q.answers[0].content : '暂无详尽解析' }}</div>
              </div>
              <!-- 纯文本展示结果与评语 -->
              <div class="result-display-bar display-column">
                <div class="score-display">
                  最终得分：<span class="score-num" :class="q.markScore > 0 ? 'score-win' : 'score-loss'">{{ q.markScore }}</span> / {{ q.score }} 分
                </div>
                <div v-if="q.markReview" class="review-display">
                  <i class="el-icon-chat-dot-round" /> <strong>评语：</strong>{{ q.markReview }}
                </div>
              </div>
            </div>
          </template>

          <!-- 5. 组合题 -->
          <template v-else-if="q.typeCode === 5">
            <div class="question-stem material-stem">
              <span class="material-tag">【组合材料】</span>
              <div class="q-text" style="white-space: pre-wrap;">{{ q.content }}</div>
            </div>

            <div class="sub-questions-container">
              <div v-for="subQ in q.children" :key="subQ.questionId" class="sub-question-item">
                <div class="question-stem sub-stem">
                  <span class="q-number">{{ subQ.number }}.</span>
                  <span class="q-score-highlight">（{{ subQ.score }}分）</span>
                  <span class="q-text">{{ subQ.content }}</span>
                  <el-tag size="mini" effect="plain" type="info" style="margin-left:5px;">{{ subQ.typeStr }}</el-tag>
                </div>

                <div class="question-body">
                  <!-- 组合题 - 选择题型子题 -->
                  <template v-if="subQ.typeCode === 1 || subQ.typeCode === 2">
                    <div class="options-display-list">
                      <div
                        v-for="opt in subQ.answers"
                        :key="opt.pos"
                        class="mark-option-item"
                        :class="{
                          'opt-correct': opt.correct,
                          'opt-user-selected': (subQ.userAnswer || []).includes(opt.pos),
                          'opt-user-wrong': (subQ.userAnswer || []).includes(opt.pos) && !opt.correct
                        }"
                      >
                        <i v-if="opt.correct" class="el-icon-success text-success" />
                        <i v-else-if="(subQ.userAnswer || []).includes(opt.pos)" class="el-icon-error text-danger" />
                        <i v-else class="el-icon-circle-check text-muted" />
                        <span class="opt-content">{{ opt.content }}</span>
                      </div>
                    </div>
                    <div class="result-display-bar">
                      <div class="score-tag-wrapper">
                        结果判定：
                        <el-tag :type="subQ.markScore === subQ.score ? 'success' : (subQ.markScore > 0 ? 'warning' : 'danger')" size="mini" effect="dark">
                          得分：{{ subQ.markScore }} 分 / 满分 {{ subQ.score }} 分
                        </el-tag>
                      </div>
                    </div>
                  </template>

                  <!-- 组合题 - 主观题型子题 (填空/问答) -->
                  <template v-else-if="subQ.typeCode === 3 || subQ.typeCode === 4">
                    <el-row :gutter="20" class="compare-row mb-10">
                      <el-col :span="12">
                        <div class="answer-block user-answer-block">
                          <div class="block-title">考生答案</div>
                          <div class="block-content text-pre-wrap">{{ (subQ.userAnswer && subQ.userAnswer[0]) || '（未作答）' }}</div>
                        </div>
                      </el-col>
                      <el-col :span="12">
                        <div class="answer-block standard-answer-block">
                          <div class="block-title">参考答案</div>
                          <div class="block-content text-pre-wrap">
                            <template v-if="subQ.typeCode === 3">
                              <div v-for="(opt, oIdx) in subQ.answers" :key="oIdx">{{ opt.content }}</div>
                            </template>
                            <template v-else>
                              {{ subQ.options && subQ.options[0] ? subQ.options[0].content : '无解析' }}
                            </template>
                          </div>
                        </div>
                      </el-col>
                    </el-row>
                    <div class="result-display-bar display-column">
                      <div class="score-display">
                        最终得分：<span class="score-num" :class="subQ.markScore > 0 ? 'score-win' : 'score-loss'">{{ subQ.markScore }}</span> / {{ subQ.score }} 分
                      </div>
                      <div v-if="subQ.markReview" class="review-display">
                        <i class="el-icon-chat-dot-round" /> <strong>评语：</strong>{{ subQ.markReview }}
                      </div>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </template>

        </div>
      </div>
    </el-main>
  </el-container>
</template>

<script>
import { getExamResult } from '@/api/exam'

export default {
  name: 'ExamResult',
  data() {
    return {
      queryParams: {
        examId: 0
      },
      currentActiveId: null,
      paperInfo: {},
      sectionsList: []
    }
  },
  created() {
    document.title = '试卷结果'
    const examId = this.$route.query.examId
    if (!examId) {
      this.$message.error('参数缺失，无法加载试卷结果页面！')
      return
    }
    this.queryParams.examId = examId
    this.loadPaperData()
  },
  methods: {
    async loadPaperData() {
      const response = await getExamResult(this.queryParams)
      const paperDetail = response.data.paperDetail || {}
      this.paperInfo = {
        title: paperDetail.title,
        subjectName: paperDetail.subjectName
      }

      let questionNumberCounter = 1
      this.sectionsList = (paperDetail.sections || []).map(section => {
        const flatQuestions = []

        const processedQuestions = (section.questions || []).map(q => {
          q.number = questionNumberCounter++

          if (q.typeCode === 5 && q.children) {
            q.children.forEach((child, index) => {
              child.number = `${q.number}.${index + 1}`
              flatQuestions.push({
                questionId: child.questionId,
                number: child.number,
                markScore: child.markScore,
                score: child.score
              })
            })
          } else {
            flatQuestions.push({
              questionId: q.questionId,
              number: q.number,
              markScore: q.markScore,
              score: q.score
            })
          }
          return q
        })

        return {
          ...section,
          questions: processedQuestions,
          flatQuestions: flatQuestions
        }
      })
    },

    getScoreClass(q) {
      const classes = []
      if (this.currentActiveId === q.questionId) {
        classes.push('is-active')
      }

      if (q.markScore === q.score) {
        classes.push('score-full')
      } else if (q.markScore > 0 && q.markScore < q.score) {
        classes.push('score-part')
      } else {
        classes.push('score-zero')
      }
      return classes
    },

    scrollToQuestion(id) {
      this.currentActiveId = id
      const el = document.getElementById('q-' + id)
      if (el) {
        // 全局滚动条模式下，靠最外层的 window 或 html/body 滚动
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    },

    formatStem(content) {
      if (!content) return ''
      return content.replace(/\n/g, '<br/>')
    }
  }
}
</script>

<style scoped>
/* 核心改动：移除了 height: 100vh 限制，允许被内容撑高，交由最外层产生全局滚动条 */
.exam-container {
  min-height: 100vh;
  background-color: #f5f7fa;
  display: flex;
  align-items: flex-start; /* 关键：防止 Aside 默认拉伸填满不正常的高度 */
}

/* 核心改动：左侧答题卡启用 position: sticky。这样在页面整体向下滚动时，答题卡能悬浮固定在视口顶部 */
.exam-aside {
  background: #fff;
  border-right: 1px solid #e6ebf5;
  padding: 15px;
  position: sticky;
  top: 20px; /* 距离浏览器视口顶部的悬浮间距 */
  max-height: calc(100vh - 40px); /* 限制左侧自身最大高度防止其超出视口 */
  overflow-y: auto; /* 若答题卡题号过多，左侧内部可自适应微调滚动 */
}

.aside-card {
  height: 100%;
}
.card-title {
  font-size: 16px;
  margin-bottom: 15px;
  color: #303133;
  border-left: 4px solid #409eff;
  padding-left: 8px;
}
.card-group {
  margin-bottom: 20px;
}
.group-label {
  font-size: 13px;
  color: #909399;
  margin-bottom: 10px;
}
.number-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.number-item {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  border: 1px solid #dcdfe6;
  transition: all 0.2s;
}

.score-full {
  background-color: #f0f9eb;
  border-color: #c2e7b0;
  color: #67c23a;
}
.score-part {
  background-color: #fdf6ec;
  border-color: #f5dab1;
  color: #e6a23c;
}
.score-zero {
  background-color: #fef0f0;
  border-color: #fde2e2;
  color: #f56c6c;
}
.number-item.is-active {
  box-shadow: 0 0 0 2px #409eff inset;
  font-weight: bold;
}

.score-legend {
  margin-top: 30px;
  border-top: 1px dashed #e6ebf5;
  padding-top: 15px;
  font-size: 12px;
  color: #606266;
}
.legend-item {
  display: inline-flex;
  align-items: center;
  margin-right: 15px;
}
.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 4px;
}
.dot.full { background: #67c23a; }
.dot.part { background: #e6a23c; }
.dot.zero { background: #f56c6c; }

/* 核心改动：移除 overflow-y: auto，交给全局。由 flex: 1 自动分配右侧空间 */
.exam-main {
  padding: 20px 40px;
  flex: 1;
}

.paper-header {
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px 0 rgba(0,0,0,0.05);
}
.paper-title {
  margin: 0 0 10px 0;
  font-size: 22px;
}
.paper-meta {
  color: #909399;
  font-size: 14px;
  display: flex;
  justify-content: space-between;
}
.question-group-section {
  margin-top: 30px;
}
.group-header-title {
  font-size: 16px;
  color: #303133;
  margin-bottom: 15px;
}
.group-desc {
  font-weight: normal;
  color: #909399;
}
.question-card {
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
  border: 1px solid #efeef0;
  transition: all 0.2s;
}
.question-card.is-active {
  border-color: #c0c4cc;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
}
.question-stem {
  font-size: 15px;
  line-height: 1.6;
  margin-bottom: 15px;
  color: #303133;
}
.q-number {
  font-weight: bold;
  margin-right: 5px;
}
.q-score-highlight {
  color: #409eff;
  font-weight: 500;
}
.mark-option-item {
  padding: 10px 15px;
  border: 1px solid #f2f6fc;
  background-color: #fafafa;
  border-radius: 4px;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
}
.opt-correct {
  background-color: #f0f9eb;
  border-color: #c2e7b0;
}
.opt-user-selected {
  border-left: 4px solid #409eff;
}
.opt-user-wrong {
  background-color: #fef0f0;
  border-color: #fde2e2;
}
.opt-content {
  margin-left: 10px;
  flex: 1;
}
.mini-tag {
  margin-left: 8px;
}

.result-display-bar {
  margin-top: 15px;
  padding: 12px 15px;
  background-color: #f8fafc;
  border-radius: 6px;
  border-left: 4px solid #909399;
}
.result-display-bar.display-column {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.score-display {
  font-size: 14px;
  color: #606266;
}
.score-num {
  font-size: 18px;
  font-weight: bold;
}
.score-win { color: #67c23a; }
.score-loss { color: #f56c6c; }

.review-display {
  font-size: 13px;
  color: #e6a23c;
  background: #fffdf9;
  padding: 8px 12px;
  border-radius: 4px;
  border: 1px dashed #f5dab1;
}

.answer-block {
  border: 1px solid #e4e7ed;
  border-radius: 6px;
  padding: 12px;
  height: 100%;
}
.user-answer-block { background-color: #fafafa; }
.standard-answer-block { background-color: #f4f4f5; border-color: #dcdfe6; }
.block-title {
  font-size: 13px;
  font-weight: bold;
  margin-bottom: 8px;
  color: #606266;
}
.block-content {
  font-size: 14px;
  color: #303133;
}
.text-pre-wrap { white-space: pre-wrap; }
.mb-15 { margin-bottom: 15px; }

.material-stem {
  background-color: #f4f4f5;
  padding: 15px;
  border-radius: 6px;
  border-left: 4px solid #909399;
}
.material-tag {
  font-weight: bold;
  color: #909399;
}
.sub-questions-container {
  margin-top: 15px;
  padding-left: 20px;
  border-left: 2px dashed #e4e7ed;
}
.sub-question-item {
  margin-bottom: 25px;
}
</style>
